import { useEffect, useState } from 'react';

import { toDisplayRole } from '../auth/roles';
import { MAPPABLE_STORED_ROLES } from './admin-api';
import { newRule } from './admin-format';
import { DOMAIN_RE } from './manage-config';
import { EligibilityBuilder } from './EligibilityBuilder';
import type {
  OnboardingConfig,
  OnboardingProduct,
  OnboardingRole,
  OnboardingUser,
  RoleMapping,
  TenantIdpConfig,
} from './admin-api';

export interface OnboardingResult {
  bankName: string;
  charter: string;
  region: string;
  primaryContactName: string;
  primaryContactEmail: string;
  config: OnboardingConfig;
  // SCRUM-585: the SSO & Access step. All optional — a tenant saved without
  // them stays Draft and is completed later from Manage Account.
  /** null = the admin skipped SSO config (Draft-only tenant). */
  idp: TenantIdpConfig | null;
  emailDomains: string[];
  roleMappings: RoleMapping[];
}

export interface OnboardingWizardProps {
  onClose: () => void;
  /** Complete Setup: persists the tenant; resolves when created. */
  onComplete: (result: OnboardingResult) => Promise<void>;
}

/**
 * v7 STEP_NAMES + the SCRUM-585 "SSO & Access" step (IdP federation, login
 * email domains, and IdP-group → role mappings — everything a client needs to
 * actually log in after activation).
 */
const STEP_NAMES = ['Setup', 'SSO & Access', 'Intelligence', 'Products', 'Vault'] as const;

/** Shared stored-role vocabulary for mapping rules (admin-api.ts). */
const MAPPABLE_ROLES = MAPPABLE_STORED_ROLES;

const REGIONS = [
  'Southeast',
  'Mid-Atlantic',
  'Midwest',
  'Pacific Northwest',
  'Northeast',
  'Southwest',
] as const;

/** v7 Step-2 triggers with their exact tenant-configurable defaults. */
const TRIGGER_DEFAULTS = [
  {
    key: 'dormantAccount',
    name: 'Dormant Account',
    desc: 'No meaningful activity beyond configurable inactivity window',
    fields: [
      { key: 'inactivityDays', label: 'Inactivity Threshold (days)', value: '90' },
      { key: 'excludeUnder', label: 'Exclude Transactions Under', value: '$25.00' },
    ],
  },
  {
    key: 'depositFlightRisk',
    name: 'Deposit Flight Risk',
    desc: 'Significant balance decline trends indicating potential attrition',
    fields: [
      { key: 'declinePct', label: 'Decline Threshold (%)', value: '20' },
      { key: 'lookbackDays', label: 'Lookback Window (days)', value: '90' },
    ],
  },
  {
    key: 'smbGrowth',
    name: 'SMB Growth',
    desc: 'Increasing deposit velocity with cross-sell potential',
    fields: [
      { key: 'growthPct', label: 'Growth Threshold (%)', value: '15' },
      { key: 'minBalance', label: 'Min Account Balance', value: '$10,000' },
    ],
  },
  {
    key: 'relationshipGap',
    name: 'Relationship Gap',
    desc: 'Checking-to-savings ratio outside normal range, indicating a missing deposit product',
    fields: [
      { key: 'checkingThreshold', label: 'Checking Balance Threshold', value: '$10,000' },
      { key: 'maxSavingsRatio', label: 'Max Savings-to-Checking Ratio', value: '0.10' },
    ],
  },
] as const;

/** v7 Step-1 default role catalog. */
const DEFAULT_ROLES: OnboardingRole[] = [
  { name: 'Bank Executive', description: 'Full platform access including settings' },
  { name: 'Bank Officer', description: 'Intelligence, Campaigns, Vault, Referrals' },
  { name: 'Teller', description: 'Referrals widget only — embedded in teller workflow' },
];

/** v7 Step-4 default products. */
const DEFAULT_PRODUCTS: OnboardingProduct[] = [
  {
    name: 'Home Equity Line of Credit',
    category: 'Lending',
    description: 'Flexible line of credit secured by home equity.',
    rules: [
      { field: 'is_homeowner', operator: 'Is homeowner', value: '', required: false },
      { field: 'account_balance', operator: 'greater than', value: '15000', required: false },
    ],
    requiredMatches: 3,
  },
  {
    name: 'Business Checking Plus',
    category: 'Deposits',
    description: 'Premium business checking with cash-flow tooling.',
    rules: [
      { field: 'account_type', operator: 'is', value: 'Business', required: false },
      { field: 'smb_growth', operator: 'is true', value: '', required: false },
    ],
    requiredMatches: 2,
  },
];

const inputCls =
  'border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary h-9 w-full border px-3 text-[0.8125rem] outline-none';
const labelCls = 'text-text-2 mb-1 block text-xs font-medium';
const cardCls = 'bg-bg-2 border-border-0 rounded-tk-md border p-3';
const sectionTitleCls = 'font-display text-text-0 mb-2 text-sm font-semibold';

/**
 * The v7 New Customer Onboarding wizard (SCRUM-391): Setup, SSO &amp; Access
 * (SCRUM-585 — IdP federation + login domains + role mappings), Intelligence,
 * Products, Vault — with the numbered stepper whose completed dots become
 * checkmarks. Documented v7 deviation (plan RT9): the prototype has NO
 * validation; we require bank name + admin email on Setup and an all-or-none
 * IdP triple on SSO &amp; Access before Continue (the server validates the
 * rest). Vault files are captured as names only (no storage writes for an
 * unprovisioned tenant — plan D4); the non-SSO steps persist into
 * onboarding_config, while SSO &amp; Access lands on real aggregate fields via
 * the dedicated admin endpoints (idp / config domains / role-mappings).
 */
export function OnboardingWizard({ onClose, onComplete }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nudge, setNudge] = useState(false);

  // Step 1 — Setup
  const [bankName, setBankName] = useState('');
  const [charter, setCharter] = useState('');
  const [region, setRegion] = useState<string>('Southeast');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Role catalog is fixed in this cut (custom Add Role = follow-up scope).
  const [roles] = useState<OnboardingRole[]>(DEFAULT_ROLES);
  const [users, setUsers] = useState<OnboardingUser[]>([]);
  const [newUser, setNewUser] = useState<OnboardingUser>({
    name: '',
    email: '',
    role: 'Bank Executive',
  });

  // Step 2 — SSO & Access (SCRUM-585). Everything optional: a partially-filled
  // IdP triple is the only thing blocked (all-or-none) so a Draft is never
  // saved with a half-configured federation.
  const [idpDiscoveryUrl, setIdpDiscoveryUrl] = useState('');
  const [idpClientId, setIdpClientId] = useState('');
  const [idpClientSecretRef, setIdpClientSecretRef] = useState('');
  const [idpScopes, setIdpScopes] = useState('');
  const [domainsText, setDomainsText] = useState('');
  const [roleMappings, setRoleMappings] = useState<RoleMapping[]>([]);
  const [newMapping, setNewMapping] = useState<RoleMapping>({
    idpGroupClaim: '',
    grantedRole: MAPPABLE_ROLES[0],
  });

  // Step 3 — Intelligence (key → field key → value)
  const [triggers, setTriggers] = useState<Record<string, Record<string, string>>>(() =>
    Object.fromEntries(
      TRIGGER_DEFAULTS.map((t) => [
        t.key,
        Object.fromEntries(t.fields.map((f) => [f.key, f.value])),
      ]),
    ),
  );

  // Steps 3/4
  const [products, setProducts] = useState<OnboardingProduct[]>(DEFAULT_PRODUCTS);

  // Step 5 — staged file NAMES only (plan D4: no storage writes pre-provisioning)
  const [vaultFiles, setVaultFiles] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const setupValid = bankName.trim().length > 0 && contactEmail.trim().length > 0;

  // SSO & Access: the IdP triple is all-or-none — a Draft must never carry a
  // half-configured federation (activation is fail-closed server-side too).
  const idpFieldsFilled = [idpDiscoveryUrl, idpClientId, idpClientSecretRef].filter((v) =>
    v.trim(),
  ).length;
  // SCRUM-594: mirror the server rules client-side so failures surface before
  // the tenant is created — domains must be bare DNS names, and when domains
  // are set they must include the primary contact's email domain (the seeded
  // contact user is the only loginnable identity right after activation).
  const parsedDomains = [
    ...new Set(
      domainsText
        .split(',')
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
  const domainsShapeValid = parsedDomains.every((d) => DOMAIN_RE.test(d));
  const contactDomain = contactEmail.trim().toLowerCase().split('@').pop() ?? '';
  const contactRouted =
    parsedDomains.length === 0 || (contactDomain !== '' && parsedDomains.includes(contactDomain));
  const ssoValid =
    (idpFieldsFilled === 0 || idpFieldsFilled === 3) && domainsShapeValid && contactRouted;

  const next = () => {
    if ((step === 0 && !setupValid) || (step === 1 && !ssoValid)) {
      setNudge(true);
      setTimeout(() => {
        setNudge(false);
      }, 1400);
      return;
    }
    if (step < STEP_NAMES.length - 1) {
      setStep(step + 1);
      return;
    }
    // Complete Setup
    setSubmitting(true);
    setError(null);
    onComplete({
      bankName: bankName.trim(),
      charter: charter.trim(),
      region,
      primaryContactName: contactName.trim() || 'Primary administrator',
      primaryContactEmail: contactEmail.trim(),
      config: {
        intelligence: triggers,
        products,
        vaultFiles,
        roles,
        users,
      },
      idp:
        idpFieldsFilled === 3
          ? {
              discoveryUrl: idpDiscoveryUrl.trim(),
              clientId: idpClientId.trim(),
              clientSecretRef: idpClientSecretRef.trim(),
              scopes: idpScopes.trim() || undefined,
            }
          : null,
      emailDomains: parsedDomains,
      roleMappings,
    }).catch((e: unknown) => {
      setSubmitting(false);
      setError(e instanceof Error ? e.message : 'Creating the tenant failed.');
    });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-label="New Customer Onboarding"
        className="bg-bg-4 border-border-1 rounded-tk-lg shadow-tk-lg flex max-h-[92vh] w-[720px] max-w-[calc(100vw-32px)] flex-col border"
      >
        {/* Header */}
        <div className="border-border-0 flex items-center justify-between border-b px-5 py-4">
          <span className="font-display text-text-0 text-base font-semibold">
            New Customer Onboarding
          </span>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-text-3 hover:bg-bg-3 hover:text-text-0 rounded-tk-sm grid h-[26px] w-[26px] place-items-center"
          >
            <svg
              viewBox="0 0 16 16"
              width="12"
              height="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="4" y1="4" x2="12" y2="12" />
              <line x1="12" y1="4" x2="4" y2="12" />
            </svg>
          </button>
        </div>

        {/* Stepper — numbered dots; completed = checkmark; connectors fill. */}
        <div
          className="border-border-0 flex items-center gap-0 border-b px-6 py-3"
          aria-label="Steps"
        >
          {STEP_NAMES.map((name, i) => (
            <div key={name} className="flex flex-1 items-center last:flex-none">
              <div className="flex items-center gap-1.5">
                <span
                  data-testid={`step-dot-${String(i)}`}
                  className={`grid h-6 w-6 place-items-center rounded-full font-mono text-[0.6875rem] font-semibold transition-colors ${
                    i < step
                      ? 'bg-brand-primary text-bg-0'
                      : i === step
                        ? 'bg-brand-dim text-brand-primary border-brand-primary border'
                        : 'bg-bg-2 text-text-3'
                  }`}
                >
                  {i < step ? (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <polyline points="4 8 7 11 12 5" />
                    </svg>
                  ) : (
                    String(i + 1)
                  )}
                </span>
                <span
                  className={`hidden text-xs whitespace-nowrap sm:inline ${i === step ? 'text-text-0 font-medium' : 'text-text-3'}`}
                >
                  {name}
                </span>
              </div>
              {i < STEP_NAMES.length - 1 && (
                <div
                  className={`mx-2 h-px flex-1 ${i < step ? 'bg-brand-primary' : 'bg-border-1'}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 0 && (
            <div className="flex flex-col gap-5">
              <section>
                <h3 className={sectionTitleCls}>Organization Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls} htmlFor="ob-bank-name">
                      Bank name *
                    </label>
                    <input
                      id="ob-bank-name"
                      value={bankName}
                      placeholder="e.g. Meridian Community Bank"
                      onChange={(e) => {
                        setBankName(e.target.value);
                      }}
                      className={`${inputCls} ${nudge && !bankName.trim() ? 'border-danger' : ''}`}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ob-charter">
                      Charter
                    </label>
                    <input
                      id="ob-charter"
                      value={charter}
                      placeholder="e.g. OCC #14892"
                      onChange={(e) => {
                        setCharter(e.target.value);
                      }}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ob-region">
                      Region
                    </label>
                    <select
                      id="ob-region"
                      value={region}
                      onChange={(e) => {
                        setRegion(e.target.value);
                      }}
                      className={inputCls}
                    >
                      {REGIONS.map((r) => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              <section>
                <h3 className={sectionTitleCls}>Primary Admin Contact</h3>
                <div className="grid grid-cols-3 gap-3">
                  <input
                    aria-label="Admin contact name"
                    value={contactName}
                    placeholder="Primary administrator"
                    onChange={(e) => {
                      setContactName(e.target.value);
                    }}
                    className={inputCls}
                  />
                  <input
                    aria-label="Admin contact email"
                    type="email"
                    value={contactEmail}
                    placeholder="admin@bank.com *"
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                    }}
                    className={`${inputCls} ${nudge && !contactEmail.trim() ? 'border-danger' : ''}`}
                  />
                  <input
                    aria-label="Admin contact phone"
                    type="tel"
                    value={contactPhone}
                    placeholder="(555) 000-0000"
                    onChange={(e) => {
                      setContactPhone(e.target.value);
                    }}
                    className={inputCls}
                  />
                </div>
              </section>

              <section>
                <h3 className={sectionTitleCls}>Roles</h3>
                <div className="flex flex-col gap-2">
                  {roles.map((r) => (
                    <div key={r.name} className={cardCls}>
                      <div className="text-text-0 text-[0.8125rem] font-medium">{r.name}</div>
                      <div className="text-text-3 text-xs">{r.description}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className={sectionTitleCls}>Users</h3>
                {users.length > 0 && (
                  <ul className="mb-2 flex flex-col gap-1.5">
                    {users.map((u, i) => (
                      <li
                        key={`${u.email}-${String(i)}`}
                        className={`${cardCls} flex items-center justify-between`}
                      >
                        <span className="text-text-0 text-[0.8125rem]">
                          {u.name}{' '}
                          <span className="text-text-3 text-xs">
                            · {u.email} · {u.role}
                          </span>
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove user ${u.name}`}
                          onClick={() => {
                            setUsers(users.filter((_, j) => j !== i));
                          }}
                          className="text-text-3 hover:text-danger text-xs"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  <input
                    aria-label="New user name"
                    value={newUser.name}
                    placeholder="Full name"
                    onChange={(e) => {
                      setNewUser({ ...newUser, name: e.target.value });
                    }}
                    className={inputCls}
                  />
                  <input
                    aria-label="New user email"
                    type="email"
                    value={newUser.email}
                    placeholder="email@bank.com"
                    onChange={(e) => {
                      setNewUser({ ...newUser, email: e.target.value });
                    }}
                    className={inputCls}
                  />
                  <select
                    aria-label="New user role"
                    value={newUser.role}
                    onChange={(e) => {
                      setNewUser({ ...newUser, role: e.target.value });
                    }}
                    className={inputCls}
                  >
                    {roles.map((r) => (
                      <option key={r.name}>{r.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      if (!newUser.name.trim() || !newUser.email.trim()) return;
                      setUsers([...users, newUser]);
                      setNewUser({ name: '', email: '', role: roles[0]?.name ?? '' });
                    }}
                    className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md h-9 shrink-0 border px-3 text-xs font-medium"
                  >
                    Add User
                  </button>
                </div>
              </section>
            </div>
          )}

          {step === 1 && (
            <div className="flex flex-col gap-5">
              <p className="text-text-2 text-xs">
                Connect the bank&rsquo;s identity provider so their team can sign in with SSO. All
                fields are optional here — an account saved without them stays inactive and can be
                completed later from Manage Account.
              </p>
              <section>
                <h3 className={sectionTitleCls}>Identity Provider (OIDC)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={labelCls} htmlFor="ob-idp-discovery">
                      Discovery URL
                    </label>
                    <input
                      id="ob-idp-discovery"
                      value={idpDiscoveryUrl}
                      placeholder="https://idp.bank.com/.well-known/openid-configuration"
                      onChange={(e) => {
                        setIdpDiscoveryUrl(e.target.value);
                      }}
                      className={`${inputCls} ${nudge && !ssoValid && !idpDiscoveryUrl.trim() ? 'border-danger' : ''}`}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ob-idp-client-id">
                      Client ID
                    </label>
                    <input
                      id="ob-idp-client-id"
                      value={idpClientId}
                      placeholder="OIDC client id"
                      onChange={(e) => {
                        setIdpClientId(e.target.value);
                      }}
                      className={`${inputCls} ${nudge && !ssoValid && !idpClientId.trim() ? 'border-danger' : ''}`}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ob-idp-secret-ref">
                      Client secret name (Secrets Manager)
                    </label>
                    <input
                      id="ob-idp-secret-ref"
                      value={idpClientSecretRef}
                      placeholder="e.g. bankiq-<bank>-client-secret"
                      onChange={(e) => {
                        setIdpClientSecretRef(e.target.value);
                      }}
                      className={`${inputCls} ${nudge && !ssoValid && !idpClientSecretRef.trim() ? 'border-danger' : ''}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls} htmlFor="ob-idp-scopes">
                      Scopes (optional)
                    </label>
                    <input
                      id="ob-idp-scopes"
                      value={idpScopes}
                      placeholder="openid profile email"
                      onChange={(e) => {
                        setIdpScopes(e.target.value);
                      }}
                      className={inputCls}
                    />
                  </div>
                </div>
                <p className="text-text-3 mt-2 text-[0.6875rem]">
                  The secret itself stays in IBM Secrets Manager — enter only its secret NAME. Ask
                  the bank to register BankIQ as an OIDC app in their IdP first.
                </p>
              </section>

              <section>
                <h3 className={sectionTitleCls}>Login Email Domains</h3>
                <input
                  aria-label="Email domains"
                  value={domainsText}
                  placeholder="bank.com, corp.bank.com (comma-separated)"
                  onChange={(e) => {
                    setDomainsText(e.target.value);
                  }}
                  className={`${inputCls} ${nudge && (!domainsShapeValid || !contactRouted) ? 'border-danger' : ''}`}
                />
                {nudge && !domainsShapeValid && (
                  <p className="text-danger mt-2 text-[0.6875rem]">
                    Each entry must be a bare domain like bank.com — no https://, @, or spaces.
                  </p>
                )}
                {nudge && domainsShapeValid && !contactRouted && (
                  <p className="text-danger mt-2 text-[0.6875rem]">
                    Include the primary admin contact&rsquo;s email domain ({contactDomain}) — the
                    contact is the first sign-in-capable user after activation.
                  </p>
                )}
                <p className="text-text-3 mt-2 text-[0.6875rem]">
                  Users typing an email on one of these domains are routed to this bank&rsquo;s
                  sign-in. Each domain can belong to exactly one customer. Include the primary admin
                  contact&rsquo;s domain so they can sign in once activated.
                </p>
              </section>

              <section>
                <h3 className={sectionTitleCls}>Role Mapping</h3>
                <p className="text-text-3 mb-2 text-[0.6875rem]">
                  Map the bank&rsquo;s IdP group names to BankIQ roles. Users signing in with a
                  mapped group get that role automatically.
                </p>
                {roleMappings.length > 0 && (
                  <ul className="mb-2 flex flex-col gap-1.5">
                    {roleMappings.map((m, i) => (
                      <li
                        key={`${m.idpGroupClaim}-${String(i)}`}
                        className={`${cardCls} flex items-center justify-between`}
                      >
                        <span className="text-text-0 text-[0.8125rem]">
                          {m.idpGroupClaim}{' '}
                          <span className="text-text-3 text-xs">
                            → {toDisplayRole(m.grantedRole)}
                          </span>
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove mapping ${m.idpGroupClaim}`}
                          onClick={() => {
                            setRoleMappings(roleMappings.filter((_, j) => j !== i));
                          }}
                          className="text-text-3 hover:text-danger text-xs"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  <input
                    aria-label="IdP group name"
                    value={newMapping.idpGroupClaim}
                    placeholder="IdP group name (e.g. bank-executives)"
                    onChange={(e) => {
                      setNewMapping({ ...newMapping, idpGroupClaim: e.target.value });
                    }}
                    className={inputCls}
                  />
                  <select
                    aria-label="Mapped BankIQ role"
                    value={newMapping.grantedRole}
                    onChange={(e) => {
                      setNewMapping({ ...newMapping, grantedRole: e.target.value });
                    }}
                    className={inputCls}
                  >
                    {MAPPABLE_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {toDisplayRole(r)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const claim = newMapping.idpGroupClaim.trim();
                      if (!claim) return;
                      if (roleMappings.some((m) => m.idpGroupClaim === claim)) return;
                      setRoleMappings([...roleMappings, { ...newMapping, idpGroupClaim: claim }]);
                      setNewMapping({ idpGroupClaim: '', grantedRole: MAPPABLE_ROLES[0] });
                    }}
                    className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md h-9 shrink-0 border px-3 text-xs font-medium"
                  >
                    Add Mapping
                  </button>
                </div>
              </section>
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-3">
              <p className="text-text-2 text-xs">
                Configure the four behavioral triggers that run nightly after data ingestion. Each
                trigger uses tenant-configurable thresholds — no ML, fully deterministic.
              </p>
              {TRIGGER_DEFAULTS.map((t) => (
                <div key={t.key} className={cardCls}>
                  <div className="text-text-0 text-[0.8125rem] font-medium">{t.name}</div>
                  <div className="text-text-3 mb-2 text-xs">{t.desc}</div>
                  <div className="grid grid-cols-2 gap-3">
                    {t.fields.map((f) => (
                      <div key={f.key}>
                        <label className={labelCls} htmlFor={`trig-${t.key}-${f.key}`}>
                          {f.label}
                        </label>
                        <input
                          id={`trig-${t.key}-${f.key}`}
                          value={triggers[t.key]?.[f.key] ?? ''}
                          onChange={(e) => {
                            setTriggers({
                              ...triggers,
                              [t.key]: { ...triggers[t.key], [f.key]: e.target.value },
                            });
                          }}
                          className={inputCls}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 3 && (
            <CollectionStep
              intro="Define the products this bank offers. Each product needs eligibility criteria and communication notes for outreach."
              items={products}
              renderTitle={(p, i) => (
                <div className="mb-2 grid grid-cols-[1fr_140px] gap-2">
                  <input
                    aria-label={`Product ${String(i + 1)} name`}
                    value={p.name}
                    onChange={(e) => {
                      setProducts(
                        products.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                      );
                    }}
                    className={`${inputCls} font-medium`}
                  />
                  <select
                    aria-label={`Product ${String(i + 1)} category`}
                    value={p.category}
                    onChange={(e) => {
                      setProducts(
                        products.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)),
                      );
                    }}
                    className={inputCls}
                  >
                    {['Lending', 'Deposits', 'Savings', 'Cards'].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}
              renderBody={(p, i) => (
                <>
                  <textarea
                    aria-label={`Product ${String(i + 1)} description`}
                    value={p.description}
                    rows={2}
                    placeholder="Brief description for outreach communications..."
                    onChange={(e) => {
                      setProducts(
                        products.map((x, j) =>
                          j === i ? { ...x, description: e.target.value } : x,
                        ),
                      );
                    }}
                    className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary mb-2 w-full resize-y border px-3 py-2 text-xs outline-none"
                  />
                  <EligibilityBuilder
                    idPrefix={`product-${String(i)}`}
                    rules={p.rules}
                    onChange={(rules) => {
                      setProducts(products.map((x, j) => (j === i ? { ...x, rules } : x)));
                    }}
                  />
                </>
              )}
              onRemove={(i) => {
                setProducts(products.filter((_, j) => j !== i));
              }}
              onAdd={() => {
                setProducts([
                  ...products,
                  {
                    name: 'New Product',
                    category: 'Deposits',
                    description: '',
                    rules: [newRule()],
                    requiredMatches: 1,
                  },
                ]);
              }}
              addLabel="Add Product"
            />
          )}

          {step === 4 && (
            <div className="flex flex-col gap-3">
              <p className="text-text-2 text-xs">
                Upload initial documents to the customer&rsquo;s Vault. These files seed the
                knowledge base for AI features like NLQ. Files are staged by name and transfer once
                the tenant is provisioned.
              </p>
              <input
                id="ob-vault-input"
                type="file"
                multiple
                aria-label="Choose vault files"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    setVaultFiles([...vaultFiles, ...[...e.target.files].map((f) => f.name)]);
                  }
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => {
                  document.getElementById('ob-vault-input')?.click();
                }}
                className="border-border-1 bg-bg-3 rounded-tk-lg w-full border-2 border-dashed px-8 py-6 text-center"
              >
                <p className="text-text-2 text-[0.8125rem] font-medium">
                  Drop files here or click to browse
                </p>
              </button>
              {vaultFiles.length > 0 && (
                <ul className="flex flex-col gap-1.5">
                  {vaultFiles.map((name, i) => (
                    <li
                      key={`${name}-${String(i)}`}
                      className={`${cardCls} flex items-center justify-between`}
                    >
                      <span className="text-text-0 text-[0.8125rem]">{name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${name}`}
                        onClick={() => {
                          setVaultFiles(vaultFiles.filter((_, j) => j !== i));
                        }}
                        className="text-text-3 hover:text-danger text-xs"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && <p className="text-danger mt-3 text-xs">{error}</p>}
        </div>

        {/* Footer */}
        <div className="border-border-0 flex items-center justify-between border-t px-5 py-4">
          <span className="text-text-3 font-mono text-[0.6875rem]">
            Step {String(step + 1)} of {String(STEP_NAMES.length)} · {STEP_NAMES[step]}
          </span>
          <span className="flex gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => {
                  setStep(step - 1);
                }}
                className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium"
              >
                Back
              </button>
            )}
            <button
              type="button"
              disabled={submitting}
              onClick={next}
              className="bg-brand-primary text-bg-0 rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
            >
              {step === STEP_NAMES.length - 1
                ? submitting
                  ? 'Provisioning…'
                  : 'Complete Setup'
                : 'Continue'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

/** Shared chrome for the Products collection step. */
function CollectionStep<T>({
  intro,
  items,
  renderTitle,
  renderBody,
  onRemove,
  onAdd,
  addLabel,
}: {
  intro: string;
  items: T[];
  renderTitle: (item: T, i: number) => React.ReactNode;
  renderBody: (item: T, i: number) => React.ReactNode;
  onRemove: (i: number) => void;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-text-2 text-xs">{intro}</p>
      {items.map((item, i) => (
        <div key={i} className="bg-bg-2 border-border-0 rounded-tk-md border p-3">
          <div className="flex items-start gap-2">
            <div className="flex-1">{renderTitle(item, i)}</div>
            <button
              type="button"
              aria-label={`Remove item ${String(i + 1)}`}
              onClick={() => {
                onRemove(i);
              }}
              className="text-text-3 hover:text-danger mt-2 text-xs"
            >
              Remove
            </button>
          </div>
          {renderBody(item, i)}
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="border-border-1 text-text-2 hover:text-brand-primary hover:border-brand-primary rounded-tk-md self-start border border-dashed px-4 py-2 text-xs font-medium transition-colors"
      >
        {addLabel}
      </button>
    </div>
  );
}
